import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  BookOpen,
  Bus,
  Compass,
  HeartHandshake,
  Trophy,
  Users,
  Smile,
  ClipboardCheck,
  Home,
  Apple,
  ArrowRight,
} from 'lucide-react';
import { SectionHeading, FeatureCard, ImageBlock } from '@/components/Pieces';
import ContactForm from '@/components/ContactForm';
import FAQ from '@/components/FAQ';
import usePageMeta from '@/hooks/usePageMeta';
import { img } from '@/lib/site';

const PILLARS = [
  { icon: Trophy, title: 'After School Sports Development', desc: 'Daily sport development integrated with academic structure.' },
  { icon: BookOpen, title: 'Homework Support', desc: 'Dedicated, supervised academic blocks built into the day.' },
  { icon: Bus, title: 'Transportation', desc: 'Safe, reliable transportation from school to academy.' },
  { icon: Compass, title: 'Leadership Development', desc: 'Captain pathways, accountability systems, and decision-making.' },
  { icon: HeartHandshake, title: 'Character Building', desc: 'Discipline, respect, and team-first identity from day one.' },
  { icon: Users, title: 'Sports Leagues & Competition', desc: 'Structured leagues that build competitive identity.' },
  { icon: GraduationCap, title: 'Mentorship', desc: 'Mentor-coach relationships built around long-term outcomes.' },
  { icon: Smile, title: 'Confidence Building', desc: 'Stage time, ownership, and earned wins.' },
  { icon: ClipboardCheck, title: 'Athlete Report Cards', desc: 'Quarterly progression: athletic, academic, character.' },
  { icon: Home, title: 'Family Engagement', desc: 'Parent communication and family event programming.' },
  { icon: Apple, title: 'Healthy Lifestyle Education', desc: 'Sleep, nutrition, hydration, screen time as performance.' },
];

const PROMISES = [
  { title: 'Safe', desc: 'Supervised, structured, and built for working families.' },
  { title: 'Structured', desc: 'Daily blocks designed by athletic and academic coaches.' },
  { title: 'Elite', desc: 'Standards modeled on the best athletic development programs.' },
  { title: 'Developmental', desc: 'Designed for growth across athletics, academics, and identity.' },
];

export default function NextGenAcademyPage() {
  usePageMeta({
    title: 'Ball Raw Next Gen Academy | Youth Development & Family Support For Working Families',
    description:
      'A structured youth development, leadership, and family support ecosystem. Built for working families — and for employers who want to support their workforce families, strengthen retention, and invest in community impact.',
    path: '/next-gen-academy',
  });
  return (
    <div data-testid="page-next-gen-academy">
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={img('youth_academy_hero')} alt="Youth academy" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <p className="brp-overline brp-reveal">Ball Raw Next Gen Academy</p>
          <h1 className="brp-reveal brp-delay-1 font-display uppercase font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.92] mt-6 max-w-4xl">
            Stronger Athletes. <span className="text-[#D4AF37]">Stronger Families.</span>
          </h1>
          <p className="brp-reveal brp-delay-2 mt-8 text-lg md:text-xl text-gray-300 max-w-2xl">
            A structured youth development and family support ecosystem — built for working parents and serious young athletes.
          </p>
          <div className="brp-reveal brp-delay-3 mt-10 flex flex-wrap gap-4">
            <Link to="/partnerships#contact" className="btn-brp-primary" data-testid="academy-cta-enroll">
              Request Family Information <ArrowRight size={16} />
            </Link>
            <Link to="/partnerships" className="btn-brp-outline-light" data-testid="academy-cta-school">
              School & Org Partnerships
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Our Promise"
            title="Safe. Structured. Elite. Developmental."
            subtitle="Every part of the Academy is built around four standards. We never compromise on them."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E5]">
            {PROMISES.map((p, i) => (
              <div key={p.title} className="bg-white p-10 text-left" data-testid={`promise-${i}`}>
                <div className="text-[#D4AF37] font-display text-5xl font-bold">0{i + 1}</div>
                <h3 className="mt-4 font-display uppercase tracking-wide text-2xl font-bold">{p.title}</h3>
                <p className="mt-3 text-[#555] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Academy Pillars"
            title="An Ecosystem, Not An Afterschool Program."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <FeatureCard key={p.title} icon={p.icon} title={p.title} desc={p.desc} accent testId={`pillar-${i}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <ImageBlock src={img('divisions_academy')} alt="Academy training" ratio="aspect-[4/3]" caption="Daily Development" />
          </div>
          <div className="lg:col-span-6">
            <SectionHeading
              light
              overline="A Day In The Academy"
              title="Built For Real Working Families."
              subtitle="From school pickup to home drop-off, every block is engineered."
            />
            <ol className="mt-8 space-y-4">
              {[
                { t: 'School Pickup', d: 'Reliable, safe transportation.' },
                { t: 'Academic Block', d: 'Supervised homework and skill support.' },
                { t: 'Movement Prep', d: 'Mobility, nutrition, hydration check-in.' },
                { t: 'Sport-Specific Development', d: 'Position and skill development with mentor-coaches.' },
                { t: 'Character & Leadership', d: 'Captain-track conversations, team identity work.' },
                { t: 'Family Communication', d: 'Daily check-ins, weekly summaries, quarterly report cards.' },
              ].map((s, i) => (
                <li key={s.t} className="flex items-start gap-4">
                  <div className="font-display text-[#D4AF37] text-2xl font-bold flex-shrink-0 w-10">0{i + 1}</div>
                  <div>
                    <div className="font-display uppercase tracking-wide text-lg">{s.t}</div>
                    <div className="text-gray-400 text-sm mt-1">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto">
          <FAQ
            testIdPrefix="academy-faq"
            overline="Frequently Asked"
            title="Frequently Asked Questions"
            items={[
              {
                q: 'What is Ball Raw Next Gen Academy?',
                a: 'Ball Raw Next Gen Academy is a youth development program that combines sports performance, mentorship, and character development in a structured environment. The program helps young people grow while supporting working families.',
              },
              {
                q: 'Why do employers partner with Ball Raw Next Gen Academy?',
                a: 'Employers recognize that supporting working families can strengthen employee satisfaction, retention, and workplace culture. The academy provides an additional resource that benefits both employees and their children.',
              },
              {
                q: 'How does the academy support working families?',
                a: 'The academy provides a structured environment where young people can learn, stay active, and continue developing after school or during approved programs. This gives working parents greater peace of mind throughout the workday.',
              },
              {
                q: 'How is participant progress measured?',
                a: 'Progress is evaluated using age appropriate development goals, participation, and personal growth milestones. Families receive updates throughout the program.',
              },
              {
                q: 'How do schools and community organizations partner with the academy?',
                a: 'We collaborate with schools, municipalities, nonprofits, and employers to develop customized youth programs that support their communities and organizational goals.',
              },
              {
                q: 'How does transportation work?',
                a: 'Transportation options vary by location and program. Details are discussed during the planning process so families understand the services available.',
              },
            ]}
          />
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white" id="contact">
        <div className="max-w-[900px] mx-auto">
          <ContactForm
            formType="general"
            variant="dark"
            title="Request Family Information"
            subtitle="Tell us a little about your athlete and family schedule. A program lead will follow up."
            showRole={false}
            showInterest
            ctaLabel="Request Information"
            testIdPrefix="academy-contact"
          />
        </div>
      </section>
    </div>
  );
}
